import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthEndpoints } from 'src/@shared/endpoints/endpoints';
import { clearLocalStoragePreserving, isJwtExpired, TokenRefreshService } from 'src/@shared/auth';

/**
 * CommonService already attaches the bearer token to every call, so this interceptor only handles the
 * expired-token 401: refresh once (shared across concurrent callers), retry with the new token, or sign out.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  constructor(private tokenRefresh: TokenRefreshService, private router: Router) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.endsWith(AuthEndpoints.Refresh)) {
      return next.handle(request);
    }

    const token = localStorage.getItem('BearerToken');
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!token || !this.isExpiredTokenResponse(error, token)) {
          return throwError(() => error);
        }
        return this.tokenRefresh.refresh().pipe(
          switchMap((freshToken) => next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${freshToken}` } }))),
          catchError((refreshError: unknown) => {
            clearLocalStoragePreserving();
            sessionStorage.clear();
            void this.router.navigate(['/login']);
            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  private isExpiredTokenResponse(error: unknown, token: string): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && (error.headers.get('Token-Expired') === 'true' || isJwtExpired(token));
  }
}
